/**
 * Authenticated file downloads.
 *
 * WHY A DOWNLOAD CANNOT BE A PLAIN `<a href>`
 * The access token lives in memory only (see lib/api.ts for why), so the
 * browser cannot attach it to a navigation. The file is fetched with the token
 * in a header and handed to the browser through a short-lived object URL.
 *
 * NOTE FOR WHOEVER TOUCHES Reports.tsx NEXT: that page carries its own copy of
 * this logic, written before this module existed. It should import from here
 * instead. It was left alone deliberately — it is somebody else's file in
 * flight at the time of writing, and a drive-by refactor of a file under active
 * edit costs more than the duplication does.
 */

import { api, ApiError, getAccessToken } from './api';

/** Extract `filename="..."` from a Content-Disposition header, if present. */
function filenameFrom(header: string | null, fallback: string): string {
  const match = header?.match(/filename="?([^";]+)"?/i);
  // Strip any path component: the name comes from a response header and is
  // used as a download name, and a name like `../x` should never be honoured.
  return match?.[1]?.split(/[\\/]/).pop() || fallback;
}

/**
 * Fetch a file, refreshing the session once if the token has expired.
 *
 * `refreshPath` is any cheap authenticated GET on the same API. Calling it
 * through the shared api client is deliberate: that client already knows how to
 * refresh exactly once even when several requests fail together, and that guard
 * is load-bearing — refresh-token rotation treats a second concurrent refresh
 * as token reuse and revokes the whole session.
 */
async function fetchFile(
  path: string,
  refreshPath: string,
  signal: AbortSignal,
): Promise<Response> {
  const send = () =>
    fetch(`/api${path}`, {
      headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      credentials: 'include',
      signal,
    });

  let response = await send();
  if (response.status === 401) {
    await api.get(refreshPath, signal);
    response = await send();
  }

  if (!response.ok) {
    // Errors are still JSON even though success is a file.
    const payload = (await response.json().catch(() => null)) as {
      error?: { code?: string; details?: Record<string, unknown> };
    } | null;
    throw new ApiError(
      response.status,
      payload?.error?.code ?? 'UNKNOWN_ERROR',
      payload?.error?.details,
    );
  }
  return response;
}

/**
 * Fetch a file and save it.
 *
 * The object URL is REVOKED once the download has been handed to the browser.
 * An un-revoked object URL keeps the whole Blob alive until the page is closed,
 * so without this every download would leak its size in memory for the rest of
 * the session.
 */
export async function downloadFile(
  path: string,
  fallbackName: string,
  refreshPath: string,
  signal: AbortSignal,
): Promise<void> {
  const response = await fetchFile(path, refreshPath, signal);
  const blob = await response.blob();
  const filename = filenameFrom(response.headers.get('Content-Disposition'), fallbackName);

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    link.remove();
    // Deferred by a tick, not called inline: Safari resolves the blob URL
    // asynchronously after click(), and revoking it synchronously can abort
    // the save. Still guaranteed to run, so nothing is retained.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}
